import { useCallback, useLayoutEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { NavLink, useMatch, useParams } from 'react-router'
import useIsMount from 'helpers/useIsMount'

import { ChevronDownIcon } from '@pleio/icons/chevron-down'

import IconButton from 'js/components/IconButton'

import AddPageButton from './AddPageButton'
import styles from './Item.module.css'
import List from './List'

type ItemType = {
    link?: string
    url?: string
    title: string
    localTitle?: string
    guid: string
    children?: ItemType[]
}

type Props = {
    editMode: boolean
    item: ItemType
    depth: number
    [key: string]: any
}

const Item = ({ editMode, item, depth, ...rest }: Props) => {
    const { guid } = useParams()

    const isMount = useIsMount()

    const [showChildren, setShowChildren] = useState(false)

    const findItemByGuid = useCallback(
        (guid: string, children?: ItemType[]) => {
            if (children === undefined || !children.length) {
                return null
            }

            for (let i = 0, l = children.length; i < l; i++) {
                const child = children[i]
                if (child.guid === guid) {
                    return child
                }
                const foundItem = findItemByGuid(guid, child.children)
                if (foundItem) {
                    return foundItem
                }
            }

            return null
        },
        [],
    )

    useLayoutEffect(() => {
        if (isMount) {
            // Show children if a descendant or current item is the current page
            if (
                !!findItemByGuid(guid || '', item.children) ||
                guid === item.guid
            ) {
                setShowChildren(true)
            }
        }
    }, [isMount, guid, findItemByGuid, item])

    const toggleOpen = () => {
        setShowChildren(!showChildren)
    }

    const { title, localTitle, children } = item
    const hasChildren = children && children.length > 0

    const { t } = useTranslation()

    // Only the top-level item has an url
    const url = item.link || item.url

    const isRootPage = depth === 0

    const matchExact = useMatch({ path: url || '', end: true })

    const offset = (depth || 1) * 16

    return (
        <>
            <NavLink
                className={styles.wrapper}
                to={url || ''}
                end
                style={
                    {
                        paddingLeft: `${offset}px`,
                        paddingRight: hasChildren ? '24px' : undefined,
                        '--item-before-top': depth ? '12px' : '14px',
                        '--item-before-left': `${(depth ? depth * 16 : 16) - 9}px`,
                    } as React.CSSProperties
                }
            >
                <span className={isRootPage ? 'RootPageTitle' : ''}>
                    {localTitle || title}
                </span>
            </NavLink>

            {!isRootPage && hasChildren && (
                <IconButton
                    className={styles.iconButton}
                    variant={showChildren ? 'secondary' : 'tertiary'}
                    size="small"
                    aria-label={
                        showChildren
                            ? t('entity-cms.hide-subpages')
                            : t('entity-cms.show-subpages')
                    }
                    onClick={toggleOpen}
                >
                    <ChevronDownIcon
                        style={{
                            transform: `scaleY(${showChildren ? -1 : 1})`,
                        }}
                    />
                </IconButton>
            )}

            {editMode && matchExact && depth < 3 && (
                <AddPageButton
                    parentGuid={guid}
                    style={{
                        marginLeft: `${offset + (depth > 0 ? 16 : 0)}px`,
                    }}
                />
            )}

            {!isRootPage && hasChildren && showChildren && (
                <List
                    containerGuid={item.guid}
                    items={children}
                    depth={depth + 1}
                    editMode={editMode}
                    {...rest}
                />
            )}
        </>
    )
}

export default Item
